import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Accept } from 'react-dropzone';
import { Loader2, RotateCcw, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../../../api/axios.js';
import { API_ENDPOINTS } from '../../../api/endpoints.js';
import { AppSelect } from '../../../shared/common/AppSelect.tsx';
import { BulkDocumentUploadZone } from '../../../components/bulk/BulkDocumentUploadZone.tsx';
import { BulkExtractionProgressOverlay } from '../../../components/bulk/BulkExtractionProgressOverlay.tsx';
import { Tooltip, TooltipProvider } from '../../../admin/components/shared/Tooltip.tsx';
import { mapProcessValidationErrorsByFileDataIndex } from '../../../components/processes/validations.js';
import { getApiErrorMessage, resolve403ErrorMessage } from '../../../request/getApiErrorMessage.ts';
import {
  importControlsBulkUpload,
  type ControlFileValidationResponse,
} from '../../../request/controls/controls.api.ts';
import { controlsQueryKeys } from '../../../queries/controls/keys.ts';
import { TENANT_PATHS } from '../../../routing/paths.ts';
import { uploadFilesAndComplete } from '../../../utils/fileUpload.js';
import { MODULE_UPLOAD } from '../../../utils/moduleUpload.ts';
import {
  controlDraftRowHasAnyMergedError,
  controlDraftRowHasSelectableBlockingError,
  selectedControlDraftRowsHaveMergedErrors,
  stripControlBackendErrorsForMappedField,
  type ControlBulkDraftRow,
  type ControlDraftTableField,
} from '../controlDraftValidation.ts';
import { buildControlImportWireRow } from '../controlBulkImportWire.ts';
import { ControlDraftReviewTable } from './ControlDraftReviewTable.tsx';

const SUPPORTED_EXTENSIONS = ['xlsx', 'csv'];

const CONTROL_BULK_HEADER_VALIDATION_ERROR_MESSAGE =
  'Header validation failed. Please verify the file headers match the sample document and try again.';

const CONTROL_BULK_ACCEPT: Accept = {
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['.xlsx'],
  'text/csv': ['.csv'],
};

const DRAFT_TABLE_PAGE_SIZE = 10;
const DRAFT_TABLE_PAGE_SIZE_OPTIONS = [10, 25, 50];

const IMPORT_CONTROLS_VALIDATION_TOOLTIP =
  'Before importing, please fix validation errors on the selected rows.';

const DRAFT_VALIDITY_FILTER_OPTIONS = [
  { value: 'all', label: 'All rows' },
  { value: 'invalid', label: 'Invalid rows' },
  { value: 'valid', label: 'Valid rows' },
] as const;

const DRAFT_ROW_VALIDATION_OPTS = { showBackendErrors: true, applyFrontendValidation: true } as const;

const CONTROL_FILE_MODULE_UPLOAD = MODULE_UPLOAD.CONTROL_WORDING;

type BulkOverlayState = {
  open: boolean;
  statusMessage: string;
  detailMessage: string | null;
  isComplete: boolean;
};

const sleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

const normalizeHeaderKey = (key: string): string =>
  String(key || '')
    .toLowerCase()
    .replace(/[\s_-]+/g, '');

const getFirstValue = (row: Record<string, unknown> | undefined, keys: string[]): string => {
  if (!row || typeof row !== 'object') return '';
  for (const key of keys) {
    if (row[key] !== undefined && row[key] !== null) {
      return String(row[key]).trim();
    }
  }
  const entries = Object.entries(row);
  for (const key of keys) {
    const want = normalizeHeaderKey(key);
    const hit = entries.find(([k]) => normalizeHeaderKey(k) === want);
    if (hit && hit[1] !== undefined && hit[1] !== null) {
      return String(hit[1]).trim();
    }
  }
  return '';
};

function defaultSelectedControlIdsForValidRows(rows: ControlBulkDraftRow[]): Set<string> {
  const getRowVal = (r: ControlBulkDraftRow, f: ControlDraftTableField): unknown => r[f];
  return new Set(
    rows
      .filter((r) => !controlDraftRowHasAnyMergedError(r, getRowVal, DRAFT_ROW_VALIDATION_OPTS))
      .map((r) => r.id)
  );
}

function mapFileDataToControlDraftRows(
  fileData: Array<Record<string, unknown>> | undefined
): ControlBulkDraftRow[] {
  return (Array.isArray(fileData) ? fileData : []).map((row, index) => ({
    id: `control-upload-${Date.now()}-${index}`,
    clientControlId: getFirstValue(row, [
      'client_control_id',
      'Control ID (Client ID)',
      'Client Referral ID',
    ]),
    title: getFirstValue(row, ['title', 'Control Title']),
    controlWordingText: getFirstValue(row, ['control_wording_text', 'Control Description']),
    stagedRow: row,
  }));
}

function mergeControlDraftErrors(
  rows: ControlBulkDraftRow[],
  errorRows: ControlFileValidationResponse['error_rows']
): ControlBulkDraftRow[] {
  const byIndex = mapProcessValidationErrorsByFileDataIndex(errorRows);
  return rows.map((row, index) => ({
    ...row,
    validation_errors: byIndex[index] || [],
  }));
}

export const ControlBulkUpload: React.FC<{ returnTo?: string }> = ({
  returnTo = TENANT_PATHS.SOX_CONTROLS,
}) => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [bulkOverlay, setBulkOverlay] = useState<BulkOverlayState>({
    open: false,
    statusMessage: '',
    detailMessage: null,
    isComplete: false,
  });
  const [uploadBusy, setUploadBusy] = useState(false);
  const [uploadHeaderErrorMessage, setUploadHeaderErrorMessage] = useState<string | null>(null);
  const [validationResult, setValidationResult] = useState<ControlFileValidationResponse | null>(null);
  const [draftRows, setDraftRows] = useState<ControlBulkDraftRow[]>([]);
  const [controlEdited, setControlEdited] = useState<
    Record<string, Partial<Record<ControlDraftTableField, string>>>
  >({});
  const [controlSelectedIds, setControlSelectedIds] = useState<Set<string>>(new Set());
  const [controlEditingField, setControlEditingField] = useState<{ rowId: string; field: string } | null>(
    null
  );
  const [stagedFileId, setStagedFileId] = useState<string | null>(null);
  const [draftSearchQuery, setDraftSearchQuery] = useState('');
  const [draftValidityFilter, setDraftValidityFilter] = useState<'all' | 'invalid' | 'valid'>('all');

  const importMutation = useMutation({
    mutationFn: importControlsBulkUpload,
    onSuccess: async (data) => {
      const body = data && typeof data === 'object' ? data : {};
      toast.success(String(body.message || 'Controls imported as drafts.'));
      await queryClient.invalidateQueries({ queryKey: controlsQueryKeys.all });
      navigate(returnTo);
    },
    onError: (error: Error) => {
      const err = error as {
        response?: { data?: { error?: string; validation?: ControlFileValidationResponse } };
      };
      const validation = err?.response?.data?.validation;
      if (validation && typeof validation === 'object') {
        setValidationResult(validation);
        if (validation.header_valid && Array.isArray(validation.file_data)) {
          const merged = mergeControlDraftErrors(
            mapFileDataToControlDraftRows(validation.file_data),
            validation.error_rows
          );
          setDraftRows(merged);
          setControlSelectedIds(defaultSelectedControlIdsForValidRows(merged));
        } else {
          setDraftRows([]);
          setControlSelectedIds(new Set());
        }
        setControlEdited({});
        setControlEditingField(null);
        resetDraftFilters();
      }
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error, 'Import failed.')));
    },
  });

  const getControlFieldValue = React.useCallback(
    (row: ControlBulkDraftRow, field: ControlDraftTableField): unknown => {
      const edits = controlEdited[row.id];
      if (edits && edits[field] !== undefined) return edits[field];
      return row[field];
    },
    [controlEdited]
  );

  const updateControlField = (rowId: string, field: string, value: string) => {
    const f = field as ControlDraftTableField;
    setControlEdited((prev) => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [f]: value },
    }));
    setDraftRows((rows) =>
      rows.map((r) => {
        if (r.id !== rowId) return r;
        const stripped = stripControlBackendErrorsForMappedField(r.validation_errors, f);
        return stripped === r.validation_errors ? r : { ...r, validation_errors: stripped };
      })
    );
  };

  const isControlFieldEditing = (rowId: string, field: string) =>
    controlEditingField?.rowId === rowId && controlEditingField?.field === field;

  const startControlEditing = (rowId: string, field: string) => {
    setControlEditingField({ rowId, field });
  };

  const stopControlEditing = () => {
    setTimeout(() => setControlEditingField(null), 100);
  };

  const validationOpts = useMemo(
    () => ({
      showBackendErrors: true,
      applyFrontendValidation: true,
    }),
    []
  );

  const selectedHasValidationErrors = useMemo(
    () =>
      selectedControlDraftRowsHaveMergedErrors(draftRows, controlSelectedIds, getControlFieldValue, {
        showBackendErrors: validationOpts.showBackendErrors,
        applyFrontendValidation: validationOpts.applyFrontendValidation,
      }),
    [draftRows, controlSelectedIds, getControlFieldValue, validationOpts]
  );

  useEffect(() => {
    if (draftRows.length === 0) return;
    setControlSelectedIds((prev) => {
      const next = new Set(prev);
      let changed = false;
      for (const id of Array.from(prev)) {
        const row = draftRows.find((r) => r.id === id);
        if (!row) {
          next.delete(id);
          changed = true;
          continue;
        }
        if (controlDraftRowHasSelectableBlockingError(row, getControlFieldValue, validationOpts)) {
          next.delete(id);
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [draftRows, controlEdited, getControlFieldValue, validationOpts]);

  const displayedDraftRows = useMemo(() => {
    let rows = draftRows;
    const q = draftSearchQuery.trim().toLowerCase();
    if (q) {
      rows = rows.filter((row) => {
        const clientId = String(getControlFieldValue(row, 'clientControlId') ?? '').toLowerCase();
        const title = String(getControlFieldValue(row, 'title') ?? '').toLowerCase();
        const desc = String(getControlFieldValue(row, 'controlWordingText') ?? '').toLowerCase();
        return clientId.includes(q) || title.includes(q) || desc.includes(q);
      });
    }
    if (draftValidityFilter === 'invalid') {
      rows = rows.filter((r) => controlDraftRowHasAnyMergedError(r, getControlFieldValue, validationOpts));
    } else if (draftValidityFilter === 'valid') {
      rows = rows.filter((r) => !controlDraftRowHasAnyMergedError(r, getControlFieldValue, validationOpts));
    }
    return rows;
  }, [draftRows, draftSearchQuery, draftValidityFilter, getControlFieldValue, validationOpts]);

  const handleControlToggleAll = useCallback(
    (checked: boolean) => {
      const displayedIds = new Set(displayedDraftRows.map((row) => row.id));
      setControlSelectedIds((prev) => {
        if (checked) {
          const selectableIds = displayedDraftRows
            .filter(
              (row) =>
                !controlDraftRowHasSelectableBlockingError(row, getControlFieldValue, validationOpts)
            )
            .map((row) => row.id);
          return new Set([...prev, ...selectableIds]);
        }
        return new Set([...prev].filter((id) => !displayedIds.has(id)));
      });
    },
    [displayedDraftRows, getControlFieldValue, validationOpts]
  );

  const draftFiltersActive = draftSearchQuery.trim() !== '' || draftValidityFilter !== 'all';

  const resetDraftFilters = useCallback(() => {
    setDraftSearchQuery('');
    setDraftValidityFilter('all');
  }, []);

  const resetControlBulkUploadSession = useCallback(() => {
    setDraftRows([]);
    setControlEdited({});
    setControlSelectedIds(new Set());
    setControlEditingField(null);
    setStagedFileId(null);
    setValidationResult(null);
    setUploadHeaderErrorMessage(null);
    resetDraftFilters();
    setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
    toast.success('Review cleared. Upload a new spreadsheet when you are ready.');
  }, [resetDraftFilters]);

  const handleDownloadControlSample = async () => {
    try {
      const response = await axiosInstance.get(API_ENDPOINTS.getSampleExcelTemplate('control'), {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'control_upload_sample.xlsx';
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      console.error('Control template download failed:', error);
      toast.error(getApiErrorMessage(error as Error, 'Failed to download sample'));
    }
  };

  const runControlBulkUpload = async (acceptedFiles: File[]) => {
    const file = acceptedFiles?.[0];
    if (!file) return;
    const ext = file.name?.split('.').pop()?.toLowerCase() || '';
    if (!SUPPORTED_EXTENSIONS.includes(ext)) {
      toast.error('Use .xlsx or .csv');
      return;
    }

    setUploadHeaderErrorMessage(null);
    setBulkOverlay({
      open: true,
      statusMessage: 'Uploading file…',
      detailMessage: file.name,
      isComplete: false,
    });
    setUploadBusy(true);
    setStagedFileId(null);

    try {
      const userHash = localStorage.getItem('userHash') || '';
      const [uploaded] = await uploadFilesAndComplete([file], userHash, CONTROL_FILE_MODULE_UPLOAD);
      const fileId = uploaded?.file_id;
      if (!fileId) {
        throw new Error('Upload did not return file id');
      }

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Validating spreadsheet…',
        detailMessage: 'Checking headers and row data.',
      }));

      const { data } = await axiosInstance.post<ControlFileValidationResponse>(
        '/api/control-upload/validate',
        { file_id: fileId }
      );

      if (!data?.header_valid) {
        toast.error(CONTROL_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setUploadHeaderErrorMessage(CONTROL_BULK_HEADER_VALIDATION_ERROR_MESSAGE);
        setValidationResult(null);
        setDraftRows([]);
        setControlEdited({});
        setControlSelectedIds(new Set());
        setControlEditingField(null);
        setStagedFileId(null);
        resetDraftFilters();
        setBulkOverlay((prev) => ({ ...prev, open: false }));
        return;
      }

      setStagedFileId(fileId);
      setValidationResult(data);
      const mapped = mergeControlDraftErrors(mapFileDataToControlDraftRows(data.file_data), data.error_rows);
      setDraftRows(mapped);
      resetDraftFilters();
      setControlEdited({});
      setControlSelectedIds(defaultSelectedControlIdsForValidRows(mapped));
      setControlEditingField(null);

      setBulkOverlay((prev) => ({
        ...prev,
        statusMessage: 'Ready to review',
        detailMessage: null,
        isComplete: true,
      }));
      await sleep(550);
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));

      if (Number(data?.failed_rows || 0) > 0) {
        toast.error('Some rows failed validation. Review the highlighted cells before importing.');
      } else {
        toast.success('File validated. Select rows to import, or edit cells as needed.');
      }
    } catch (error) {
      console.error('Control upload validate failed:', error);
      toast.error(resolve403ErrorMessage(error, getApiErrorMessage(error as Error, 'Validate failed')));
      setUploadHeaderErrorMessage(null);
      setValidationResult(null);
      setDraftRows([]);
      setControlEdited({});
      setControlSelectedIds(new Set());
      setControlEditingField(null);
      setStagedFileId(null);
      resetDraftFilters();
      setBulkOverlay((prev) => ({ ...prev, open: false, isComplete: false }));
    } finally {
      setUploadBusy(false);
    }
  };

  const handleImport = () => {
    if (controlSelectedIds.size === 0) {
      toast.error('Select at least one control row to import.');
      return;
    }
    if (selectedHasValidationErrors) {
      toast.error('Fix validation issues on selected rows before importing.');
      return;
    }

    const selected = draftRows.filter((r) => controlSelectedIds.has(r.id));
    const file_data = selected.map((row) =>
      buildControlImportWireRow(row, getControlFieldValue, validationOpts)
    );
    importMutation.mutate({
      ...(stagedFileId ? { file_id: stagedFileId } : {}),
      file_data,
    });
  };

  const isImporting = importMutation.isPending;

  const isImportDisabled =
    isImporting ||
    controlSelectedIds.size === 0 ||
    selectedHasValidationErrors ||
    !validationResult?.header_valid;

  const showImportValidationTooltip = controlSelectedIds.size > 0 && selectedHasValidationErrors;

  return (
    <div className="mt-5">
      <BulkExtractionProgressOverlay
        open={bulkOverlay.open}
        title="Processing Bulk Upload"
        subtitle="We are uploading your spreadsheet and validating each row before you review and import controls."
        statusMessage={bulkOverlay.statusMessage}
        detailMessage={bulkOverlay.detailMessage}
        isComplete={bulkOverlay.isComplete}
      />

      {draftRows.length === 0 ? (
        <BulkDocumentUploadZone
          accept={CONTROL_BULK_ACCEPT}
          multiple={false}
          disabled={uploadBusy || isImporting}
          sectionTitle="Bulk upload controls"
          sectionSubtitle="Upload a spreadsheet (.xlsx or .csv). We validate the file, map rows to controls, and show them below for review before you import them."
          cardTitle="Upload control spreadsheet"
          cardSubtitle="Drop a file here or choose one from your computer. Same flow as process and risk bulk upload."
          formatsFootnote="Supports Excel (.xlsx) and CSV (.csv)."
          chooseFilesLabel="Choose Files"
          onDownloadSample={handleDownloadControlSample}
          onDropAccepted={runControlBulkUpload}
          inlineAlertMessage={uploadHeaderErrorMessage}
        />
      ) : null}

      {draftRows.length > 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 px-6 py-4">
            <div className="min-w-0 flex-1">
              <h2 className="text-lg font-medium text-gray-900">
                {controlSelectedIds.size > 0
                  ? `${controlSelectedIds.size} of ${draftRows.length} selected`
                  : `Extracted Controls (${draftRows.length})`}
              </h2>
              <p className="mt-1 text-sm text-gray-500">
                Review the extracted rows, fix any highlighted validation issues, then select the controls you want to
                import.
              </p>
            </div>
            <button
              type="button"
              onClick={resetControlBulkUploadSession}
              disabled={uploadBusy || isImporting}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              aria-label="Clear uploaded file and return to upload step"
            >
              <RotateCcw className="h-3.5 w-3.5" aria-hidden />
              Upload New
            </button>
          </div>

          <div className="border-b border-gray-200 bg-gray-50/90 px-4 py-3 sm:px-6">
            <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between lg:gap-4">
              <div className="relative min-w-0 flex-1 lg:max-w-md">
                <Search
                  className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  aria-hidden
                />
                <input
                  type="search"
                  placeholder="Search by client ID, title, or description…"
                  value={draftSearchQuery}
                  onChange={(e) => setDraftSearchQuery(e.target.value)}
                  className="w-full rounded-lg border border-gray-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                  aria-label="Search extracted controls"
                />
              </div>
              <div className="flex flex-wrap items-end gap-2">
                <AppSelect
                  wrapperClassName="min-w-[11rem]"
                  size="control"
                  value={draftValidityFilter}
                  onChange={(e) => setDraftValidityFilter(e.target.value as 'all' | 'invalid' | 'valid')}
                  aria-label="Filter by validation status"
                  options={[...DRAFT_VALIDITY_FILTER_OPTIONS]}
                />
                {draftFiltersActive ? (
                  <button
                    type="button"
                    onClick={resetDraftFilters}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                  >
                    Clear filters
                  </button>
                ) : null}
              </div>
            </div>
            {displayedDraftRows.length !== draftRows.length ? (
              <p className="mt-2 text-xs text-gray-500">
                Showing {displayedDraftRows.length} of {draftRows.length} rows
              </p>
            ) : null}
          </div>

          <ControlDraftReviewTable
            rows={displayedDraftRows}
            selectedIds={controlSelectedIds}
            onToggleAll={handleControlToggleAll}
            selectAllAriaLabel="Select all rows without client referral ID or title validation errors"
            onToggleRow={(rowId, checked) => {
              if (checked) {
                const row = draftRows.find((r) => r.id === rowId);
                if (row && controlDraftRowHasSelectableBlockingError(row, getControlFieldValue, validationOpts)) {
                  return;
                }
              }
              setControlSelectedIds((prev) => {
                const next = new Set(prev);
                if (checked) next.add(rowId);
                else next.delete(rowId);
                return next;
              });
            }}
            rowSelectAriaLabel={(row) =>
              `Select control ${getControlFieldValue(row, 'title') || row.id}`
            }
            getFieldValue={getControlFieldValue}
            isFieldEditing={isControlFieldEditing}
            startEditing={startControlEditing}
            stopEditing={stopControlEditing}
            updateField={updateControlField}
            showBackendValidationErrors
            applyFrontendValidation
            initialPageSize={DRAFT_TABLE_PAGE_SIZE}
            pageSizeOptions={DRAFT_TABLE_PAGE_SIZE_OPTIONS}
          />

          <div className="flex items-center justify-end border-t border-gray-200 px-6 py-3">
            <TooltipProvider>
              {showImportValidationTooltip ? (
                <Tooltip content={IMPORT_CONTROLS_VALIDATION_TOOLTIP} side="top">
                  <span className="inline-flex">
                    <button
                      type="button"
                      onClick={() => void handleImport()}
                      disabled={isImportDisabled}
                      className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      {isImporting ? 'Importing…' : 'Import selected controls'}
                    </button>
                  </span>
                </Tooltip>
              ) : (
                <button
                  type="button"
                  title={
                    controlSelectedIds.size === 0
                      ? 'Select at least one row to import.'
                      : !validationResult?.header_valid
                        ? 'File did not pass header validation.'
                        : undefined
                  }
                  onClick={() => void handleImport()}
                  disabled={isImportDisabled}
                  className="inline-flex items-center gap-2 rounded-lg bg-gray-900 px-4 py-2 text-sm font-medium text-white hover:bg-gray-800 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isImporting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                  {isImporting ? 'Importing…' : 'Import selected controls'}
                </button>
              )}
            </TooltipProvider>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default ControlBulkUpload;
